'use client';

import React, { useRef, useState, useEffect } from 'react';

import Header from '@/components/common/Header';

import Icon from '@/components/ui/AppIcon';

import HeroSection from './HeroSection';

import FeaturedPuppies from './FeaturedPuppies';

import TrustIndicators from './TrustIndicators';

import VarietiesShowcase from './VarietiesShowcase';

import TestimonialsPreview from './TestimonialsPreview';

import CTASection from './CTASection';

import Footer from './Footer';

import {
  trackLeadFormSubmission,
  trackCTAClick,
  trackScrollDepth,
  trackExitIntentPopup,
} from '@/lib/seo/conversionTracking';

const HomeLandingInteractive = () => {
  const formRef = useRef<HTMLDivElement>(null);

  const [showExitPopup, setShowExitPopup] = useState(false);

  const [showStickyBar, setShowStickyBar] = useState(false);

  const [hasSeenExitPopup, setHasSeenExitPopup] = useState(false);

  // Lead form state

  const [formData, setFormData] = useState({
    name: '',

    email: '',

    phone: '',

    message: '',

    preferredPuppy: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Track scroll depth for engagement
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let scrollDepths = [25, 50, 75, 90];
    const trackedDepths = new Set<number>();

    const handleScrollTracking = () => {
      const scrollPercentage =
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

      scrollDepths.forEach((depth) => {
        if (scrollPercentage >= depth && !trackedDepths.has(depth)) {
          trackScrollDepth(depth);

          trackedDepths.add(depth);
        }
      });
    };

    window.addEventListener('scroll', handleScrollTracking);

    return () => window.removeEventListener('scroll', handleScrollTracking);
  }, []);

  // Enhanced scroll to form with tracking
  const scrollToForm = () => {
    trackCTAClick('hero_section', 'Reserve Your Puppy Today');

    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Exit-intent detection
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasSeenExitPopup) {
        setShowExitPopup(true);

        setHasSeenExitPopup(true);

        trackExitIntentPopup('shown');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasSeenExitPopup]);

  // Sticky bar on scroll
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      if (window.scrollY > 800) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSubmitting(true);

    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
        },

        body: JSON.stringify({
          name: formData.name,

          email: formData.email,

          phone: formData.phone,

          message: formData.message,

          preferredPuppy: formData.preferredPuppy,

          formType: 'lead',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Track Google Ads conversion
      trackLeadFormSubmission({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        puppyType: formData.preferredPuppy,
        leadSource: 'home_landing_page_form',
      });

      setSubmitStatus('success');

      setFormData({ name: '', email: '', phone: '', message: '', preferredPuppy: '' });
    } catch (error) {
      console.error('Error submitting form:', error);

      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main>
        <HeroSection onScrollToForm={scrollToForm} />

        <FeaturedPuppies />

        <TrustIndicators />

        <VarietiesShowcase />

        <TestimonialsPreview />

        {/* Service Area Section */}

        <section className="bg-white py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Proudly Serving Families Nationwide
                </h2>

                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Based in Trenton, NJ, we deliver healthy, AKC-certified Labrador puppies to loving
                  families across the United States
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Primary Service Area */}

                <div className="bg-primary/5 rounded-lg p-6 border-2 border-primary">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="MapPinIcon" className="w-6 h-6 text-primary" />

                    <h3 className="text-xl font-bold text-foreground">Primary Service Area</h3>
                  </div>

                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>New Jersey (Trenton, Newark, Princeton, Atlantic City)</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>Pennsylvania (Philadelphia, Pittsburgh)</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>New York (New York City, Albany)</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>Maryland (Baltimore, Annapolis)</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>Delaware (Wilmington, Dover)</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>Virginia (Richmond, Virginia Beach)</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>Washington DC</span>
                    </li>

                    <li className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" className="w-4 h-4 text-primary" />

                      <span>Massachusetts (Boston, Worcester)</span>
                    </li>
                  </ul>
                </div>

                {/* Northeast Region */}

                <div className="bg-muted/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="MapIcon" className="w-6 h-6 text-primary" />

                    <h3 className="text-xl font-bold text-foreground">Northeast Region</h3>
                  </div>

                  <ul className="space-y-2 text-muted-foreground">
                    <li>Connecticut</li>

                    <li>Rhode Island</li>

                    <li>Vermont</li>

                    <li>New Hampshire</li>

                    <li>Maine</li>
                  </ul>
                </div>

                {/* Southeast Region */}

                <div className="bg-muted/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="MapIcon" className="w-6 h-6 text-primary" />

                    <h3 className="text-xl font-bold text-foreground">Southeast Region</h3>
                  </div>

                  <ul className="space-y-2 text-muted-foreground">
                    <li>North Carolina</li>

                    <li>South Carolina</li>

                    <li>Georgia (Atlanta)</li>

                    <li>Florida</li>

                    <li>Tennessee (Nashville)</li>

                    <li>Kentucky</li>

                    <li>Alabama</li>

                    <li>Louisiana</li>
                  </ul>
                </div>

                {/* Midwest Region */}

                <div className="bg-muted/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="MapIcon" className="w-6 h-6 text-primary" />

                    <h3 className="text-xl font-bold text-foreground">Midwest Region</h3>
                  </div>

                  <ul className="space-y-2 text-muted-foreground">
                    <li>Ohio (Columbus, Cleveland)</li>

                    <li>Michigan</li>

                    <li>Illinois (Chicago)</li>

                    <li>Indiana</li>

                    <li>Wisconsin</li>

                    <li>Minnesota</li>

                    <li>Missouri</li>
                  </ul>
                </div>

                {/* Southern Region */}

                <div className="bg-muted/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="MapIcon" className="w-6 h-6 text-primary" />

                    <h3 className="text-xl font-bold text-foreground">Southern Region</h3>
                  </div>

                  <ul className="space-y-2 text-muted-foreground">
                    <li>Texas (Dallas, Houston, Austin)</li>
                  </ul>
                </div>

                {/* Western Region */}

                <div className="bg-muted/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="MapIcon" className="w-6 h-6 text-primary" />

                    <h3 className="text-xl font-bold text-foreground">Western Region</h3>
                  </div>

                  <ul className="space-y-2 text-muted-foreground">
                    <li>California (Los Angeles, San Francisco)</li>

                    <li>Arizona</li>

                    <li>Colorado (Denver)</li>

                    <li>Washington (Seattle)</li>

                    <li>Oregon</li>

                    <li>Nevada</li>
                  </ul>
                </div>
              </div>

              <div className="mt-12 text-center">
                <div className="bg-primary/10 rounded-lg p-6 max-w-2xl mx-auto">
                  <Icon name="TruckIcon" className="w-8 h-8 text-primary mx-auto mb-3" />

                  <h3 className="text-xl font-bold text-foreground mb-2">
                    Safe Puppy Delivery Available
                  </h3>

                  <p className="text-muted-foreground">
                    We offer safe, professional ground transportation to families across the United
                    States. Contact us to discuss delivery options to your state.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lead Capture Form */}

        <section ref={formRef} className="bg-amber-50 py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl">
              <div className="rounded-2xl bg-white p-8 lg:p-12 shadow-warm-xl">
                <div className="text-center mb-8">
                  <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                    Ready to Welcome a Labrador Puppy?
                  </h2>

                  <p className="text-lg text-muted-foreground">
                    Fill out the form below and we'll help you find your perfect companion. We
                    typically respond within 24 hours!
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 flex items-start gap-3 rounded-md bg-success/10 p-4 text-success border border-success/20">
                    <Icon name="CheckCircleIcon" size={20} variant="solid" />

                    <p className="text-sm font-medium">
                      Thank you! Your message has been sent successfully. We'll get back to you
                      within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 flex items-start gap-3 rounded-md bg-destructive/10 p-4 text-destructive border border-destructive/20">
                    <Icon name="XCircleIcon" size={20} variant="solid" />

                    <p className="text-sm font-medium">
                      Sorry, there was an error sending your message. Please try again or email us
                      directly at contact@luminexlabradors.com
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="lead-name"
                        className="mb-2 block text-sm font-medium text-foreground"
                      >
                        Full Name *
                      </label>

                      <input
                        type="text"
                        id="lead-name"
                        name="Name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="lead-email"
                        className="mb-2 block text-sm font-medium text-foreground"
                      >
                        Email Address *
                      </label>

                      <input
                        type="email"
                        id="lead-email"
                        name="Email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="lead-phone"
                      className="mb-2 block text-sm font-medium text-foreground"
                    >
                      Phone Number *
                    </label>

                    <input
                      type="tel"
                      id="lead-phone"
                      name="Phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full rounded-md border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="lead-puppy"
                      className="mb-2 block text-sm font-medium text-foreground"
                    >
                      Interested Puppy (Optional)
                    </label>
                    <select
                      id="lead-puppy"
                      name="PreferredPuppy"
                      value={formData.preferredPuppy}
                      onChange={(e) => setFormData({ ...formData, preferredPuppy: e.target.value })}
                      className="w-full rounded-md border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select a puppy (optional)</option>
                      <option value="FINN - Yellow Labrador Male">
                        FINN - Yellow Labrador Male
                      </option>
                      <option value="JAX - Black Labrador Male">JAX - Black Labrador Male</option>
                      <option value="CoCo - Yellow Labrador Female">
                        CoCo - Yellow Labrador Female
                      </option>
                      <option value="LuLu - Chocolate Labrador Female">
                        LuLu - Chocolate Labrador Female
                      </option>
                      <option value="Bonley - Chocolate Labrador Male">
                        Bonley - Chocolate Labrador Male
                      </option>
                      <option value="LiLi - Yellow Labrador Female">
                        LiLi - Yellow Labrador Female
                      </option>
                      <option value="BRUNO - Yellow Labrador Male">
                        BRUNO - Yellow Labrador Male
                      </option>
                      <option value="WALTERS - Chocolate Labrador Male">
                        WALTERS - Chocolate Labrador Male
                      </option>
                      <option value="Not sure yet - Help me choose">
                        Not sure yet - Help me choose
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="lead-message"
                      className="mb-2 block text-sm font-medium text-foreground"
                    >
                      Tell Us About Your Family *
                    </label>

                    <textarea
                      id="lead-message"
                      name="Message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full rounded-md border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      placeholder="Tell us about your home, family, and what you're looking for in a Labrador puppy..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-primary px-7 font-medium text-primary-foreground shadow-warm transition-smooth hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Get Started'}

                    {!isSubmitting && <Icon name="ArrowRightIcon" size={20} />}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <CTASection onScrollToForm={scrollToForm} />
      </main>

      <Footer />

      {/* Sticky Bottom Bar */}

      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-primary shadow-warm-lg border-t-2 border-accent animate-in slide-in-from-bottom duration-300">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon
                  name="SparklesIcon"
                  size={24}
                  variant="solid"
                  className="text-primary-foreground"
                />

                <div>
                  <p className="font-semibold text-primary-foreground text-sm lg:text-base">
                    🎁 Limited Time: $200 OFF + Free Starter Kit
                  </p>

                  <p className="text-xs text-primary-foreground/80">
                    Only 3 puppies remaining this month
                  </p>
                </div>
              </div>

              <button
                onClick={scrollToForm}
                className="rounded-lg bg-accent px-4 lg:px-6 py-2 lg:py-3 font-semibold text-accent-foreground transition-smooth hover:bg-accent/90 text-sm lg:text-base whitespace-nowrap"
              >
                Reserve Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent Popup */}

      {showExitPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="relative mx-4 w-full max-w-2xl rounded-2xl bg-card p-8 shadow-2xl border-2 border-accent animate-in zoom-in duration-300">
            <button
              onClick={() => setShowExitPopup(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close popup"
            >
              <Icon name="XMarkIcon" size={28} variant="outline" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                <Icon name="HeartIcon" size={32} variant="solid" className="text-accent" />
              </div>

              <h3 className="font-heading text-3xl font-bold text-foreground mb-3">
                Wait! Don't Miss Out on Your Perfect Puppy
              </h3>

              <p className="text-lg text-muted-foreground mb-2">
                Before you go, claim your exclusive offer:
              </p>

              <div className="inline-block bg-accent/10 rounded-lg px-6 py-3 border-2 border-accent/30">
                <p className="text-2xl font-bold text-accent">
                  $200 OFF + Free Premium Starter Kit
                </p>

                <p className="text-sm text-muted-foreground mt-1">
                  Worth $350+ in value • Limited to first 3 reservations
                </p>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="GiftIcon" size={20} variant="solid" className="text-accent" />
                Your Free Starter Kit Includes:
              </h4>

              <ul className="grid md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                  Premium puppy food (2-week supply)
                </li>

                <li className="flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                  Training guide & video access
                </li>

                <li className="flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                  Collar, leash & ID tag
                </li>

                <li className="flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                  Toys & comfort blanket
                </li>
              </ul>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowExitPopup(false);

                  scrollToForm();
                }}
                className="flex-1 rounded-lg bg-accent px-6 py-4 font-semibold text-accent-foreground transition-smooth hover:bg-accent/90 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              >
                Claim My Offer Now
              </button>

              <button
                onClick={() => setShowExitPopup(false)}
                className="px-6 py-4 text-muted-foreground hover:text-foreground transition-colors"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeLandingInteractive;
